import { TIE_STEP, defaultPlayersAbove, frameId, pictureLayerOrder, propDrawRow, type AssetManifest, type CompiledChunk, type ManifestObjectAppearance } from "daedalus-core";
import type { PictureLayer, PictureProp } from "daedalus-renderer-phaser";
import { chunkKey } from "../chunk-key.ts";
import { ShrineArt, landmarkProps, propReaches, type LandmarkTally } from "./landmarks.ts";

/**
 * The game's feed of the map picture painter (P1.9-T178; ADR-100 decision 3): gives the painter the layers of one
 * chunk of the booted map from the compiled client chunks the page already holds, in the drawn order the editor's
 * feed uses, so the editor's overview and the game's pictures are one drawing. Things (gates, wells, signs, training
 * dummies) are drawn from the chunks' landmark lists and the shrine art the character has discovered, through the one
 * function the Map window's feed uses (`landmarkProps`, ADR-100 decisions 54 and 59), each in the initial state's look;
 * a roof is a prop of its layer and is drawn closed. Creatures are not in a picture, and zones and doors are carried only
 * by a server chunk.
 */

/**
 * What the feed needs of the world: the tile size and the asset manifest the frames are named in.
 */
export interface FeedWorld {
  readonly tileSize: number;
  readonly assets: AssetManifest;
  readonly appearances?: readonly ManifestObjectAppearance[];
  readonly art?: ShrineArt | null;
  readonly tally?: LandmarkTally;
}

/**
 * A prop that may reach into the chunk, with the key it sorts by.
 */
interface Keyed {
  readonly prop: PictureProp;
  readonly key: number;
}

/**
 * The chunks whose props can reach into a chunk: a prop's art stands up and to the right of its anchor, so it spans
 * the chunk it is anchored in and those above and to the left of it. The chunk itself, the one to its left, the one
 * below and the one below-left, when art is no larger than a chunk.
 */
const REACHING: readonly (readonly [number, number])[] = [[0, 0], [-1, 0], [0, 1], [-1, 1]];

/**
 * The layers of one chunk of the booted map in drawn order: each tile layer's cells as the chunk holds them, and
 * each prop layer's props that reach into the chunk, from the chunk and its neighbours to the left and below, sorted
 * as the editor sorts them. The terrain layers are laid before the props, as the world draws them, under the band
 * the chunk's own layers give (`defaultPlayersAbove`), since a pinned band counts the document's layers and not the
 * chunk's. A chunk the map lacks has no layers. It allocates for the chunk it is asked for, once, when
 * the picture composes it; nothing is kept.
 *
 * @param chunks The map's client chunks by `cx,cy`.
 * @param world The tile size and the asset manifest.
 * @param cx Chunk column.
 * @param cy Chunk row.
 * @returns The layers.
 */
export function chunkPictureLayers(chunks: ReadonlyMap<string, CompiledChunk>, world: FeedWorld, cx: number, cy: number): PictureLayer[] {
  const here = chunks.get(chunkKey(cx, cy));
  if (here === undefined) {
    return [];
  }
  const size = here.chunkSize;
  const left = cx * size;
  const top = cy * size;
  const layers: PictureLayer[] = [];
  const band = defaultPlayersAbove(here.layers);
  const things = landmarkProps({ manifest: world.assets, appearances: world.appearances ?? [], tileSize: world.tileSize, chunkSize: size }, chunks, world.art ?? null, band, cx, cy, world.tally);
  for (const index of pictureLayerOrder(here.layers.map((layer) => layer.kind === "tile" && layer.variant !== null), band)) {
    const layer = here.layers[index] as CompiledChunk["layers"][number];
    if (layer.kind === "tile") {
      layers.push({ kind: "cells", cells: { tiles: layer.tiles, chunkSize: size, tileSize: world.tileSize, tilesets: here.tilesets, variant: layer.variant, manifest: world.assets, missing: () => undefined } });
    }
    if (layer.kind === "prop") {
      const keyed: Keyed[] = [...(things.get(index) ?? [])];
      for (const [dx, dy] of REACHING) {
        const near = chunks.get(chunkKey(cx + dx, cy + dy));
        const same = near?.layers.find((candidate) => candidate.kind === "prop" && candidate.id === layer.id);
        if (same === undefined || same.kind !== "prop") {
          continue;
        }
        for (const prop of same.props) {
          const id = frameId(prop.frame.tileset, null, prop.frame.tile);
          const rect = world.assets.frames[id];
          if (rect === undefined || !propReaches(prop.x, prop.y, rect.w, rect.h, world.tileSize, left, top, size)) {
            continue;
          }
          const row = propDrawRow(prop, rect, world.tileSize);
          keyed.push({ prop: { frame: id, x: prop.x, y: prop.y, flipX: prop.flipX, flipY: prop.flipY }, key: row + (prop.order ?? 0) * TIE_STEP });
        }
      }
      keyed.sort((a, b) => a.key - b.key);
      layers.push({ kind: "props", props: keyed.map((entry) => entry.prop) });
    } else if (things.has(index)) {
      layers.push({ kind: "props", props: (things.get(index) ?? []).map((entry) => entry.prop) });
    }
  }
  return layers;
}
